import { useMemo } from 'react'
import type { PrimaryGoal } from '@/types/learner-portal'

// Screen types for step calculation
export const SCREEN_TYPES = {
  INTRO: 'intro',
  LANGUAGE: 'language',
  ENGLISH: 'english',
  TIME: 'time',
  GOALS: 'goals',
  INDUSTRIES: 'industries',
  TEST_TYPE: 'testType',
  COMMUNICATION_CHANNELS: 'communicationChannels',
  COMMUNICATION_FREQUENCY: 'communicationFrequency',
  DONE: 'done',
} as const

export type ScreenType = (typeof SCREEN_TYPES)[keyof typeof SCREEN_TYPES]

/**
 * Hook to calculate which questions should be shown based on selected goals
 */
export function useConditionalQuestions(primaryGoals: readonly PrimaryGoal[]) {
  const showTestTypeQuestion = useMemo(() => primaryGoals.includes('test_prep'), [primaryGoals])

  return { showTestTypeQuestion }
}

type ScreenSequenceOptions = {
  showTestTypeQuestion: boolean
  skipEnglishLevel: boolean
}

/**
 * Hook to calculate the dynamic screen sequence based on conditional questions.
 * Skips the English ability screen when the learner already has PA data (ENG-1990).
 */
export function useScreenSequence({ showTestTypeQuestion, skipEnglishLevel }: ScreenSequenceOptions) {
  const screens = useMemo<readonly ScreenType[]>(() => {
    return [
      SCREEN_TYPES.INTRO,
      SCREEN_TYPES.LANGUAGE, // Q0
      ...(skipEnglishLevel ? [] : [SCREEN_TYPES.ENGLISH]), // Q2 (skip if PA exists)
      SCREEN_TYPES.TIME, // Q3
      SCREEN_TYPES.GOALS, // Q4
      SCREEN_TYPES.INDUSTRIES, // Q5
      ...(showTestTypeQuestion ? [SCREEN_TYPES.TEST_TYPE] : []), // Q6 (conditional)
      SCREEN_TYPES.COMMUNICATION_CHANNELS, // Q7
      SCREEN_TYPES.COMMUNICATION_FREQUENCY, // Q8
      SCREEN_TYPES.DONE,
    ]
  }, [showTestTypeQuestion, skipEnglishLevel])

  return { screens, totalSteps: screens.length }
}
